/**
 * Meeting form popup — link interception.
 *
 * Any link whose href points at the HubSpot meeting URL rendered by
 * sections/10x_meeting_form.liquid opens that popup instead of navigating away.
 * Opt-in via the section's "Open popup for every link" setting (data-meeting-intercept).
 * One delegated listener, so every CTA on the page (and any added later in the
 * theme editor) is covered without per-section settings.
 *
 * Open/close/animation behaviour is owned by blood-test-panel-modal.js: this module
 * just decides which clicks should open it.
 *
 * Contract:
 *   <dialog data-bt-modal data-meeting-url="<url>" data-meeting-intercept>   the meeting popup
 */

import { openModal } from 'JsComponents/blood-test-panel-modal.js';

const normalize = (url) => {
  try {
    const { host, pathname } = new URL(url, window.location.href);
    return `${host}${pathname.replace(/\/$/, '')}`;
  } catch (e) {
    return '';
  }
};

/**
 * HubSpot posts a { height } message each time the embed changes step. The popup panel
 * keeps its scroll position, so the next step (time slots) opened mid-way down on mobile.
 * Scroll the panel back to the top (heading included) on every step change after the first load.
 *
 * On mobile the panel doesn't scroll — HubSpot's own (cross-origin) scroller does, and we
 * can't set its scrollTop. Instead the iframe is made taller than its content for a couple
 * of frames (--reset class): the browser clamps HubSpot's scroll to 0, and it stays there
 * when the normal height returns.
 */
function initMeetingStepScroll() {
  const dialog = document.querySelector('[data-bt-modal][data-meeting-url]');
  if (!dialog) return () => {};

  let loaded = false;
  const onMessage = (e) => {
    const iframe = dialog.querySelector('iframe');
    if (!iframe || e.source !== iframe.contentWindow || typeof e.data?.height !== 'number') return;
    if (!loaded) {
      loaded = true;
      return;
    }
    const panel = dialog.querySelector('[data-bt-modal-panel]');
    if (panel) panel.scrollTo({ top: 0, behavior: 'smooth' });

    dialog.classList.add('meeting-form-modal--reset');
    setTimeout(() => dialog.classList.remove('meeting-form-modal--reset'), 1200); // message fires on click, before the next step renders
  };

  window.addEventListener('message', onMessage);
  return () => window.removeEventListener('message', onMessage);
}

export default function initMeetingFormLinks() {
  const stopStepScroll = initMeetingStepScroll();
  const dialog = document.querySelector('[data-bt-modal][data-meeting-url][data-meeting-intercept]');
  if (!dialog) return { destroy: stopStepScroll };

  const target = normalize(dialog.dataset.meetingUrl);

  const onClick = (e) => {
    if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey) return;
    const link = e.target.closest('a[href]');
    if (!link || dialog.contains(link) || normalize(link.href) !== target) return;

    e.preventDefault();
    openModal(dialog);
  };

  document.addEventListener('click', onClick);

  return {
    destroy() {
      stopStepScroll();
      document.removeEventListener('click', onClick);
    },
  };
}
